<script setup lang="ts">
/**
 * EditLinkModal — 1:1 порт docs/mockups/modal-edit-link.html (inbox-145).
 *
 * Секции мокапа (все из 1:1):
 *   1. Status row (active/paused radio toggle + статус-индикатор)
 *   2. Target URL — целевой URL
 *   3. Slug — короткий код (с prefix хостом)
 *   4. Campaign + Tags row — кампания (select) + tags multi-chip
 *   5. UTM-метки — collapsible <details> с 5 полями (open by default)
 *   6. Умная маршрутизация — collapsible, read-only список правил
 *      → UiDevCard (backend endpoint'а нет)
 *   7. Дополнительно — TTL, click_limit, password toggle + поле, internal note
 *      • password / internal note backend'а нет → UiDevCard внутри секции
 *   8. Опасная зона — кнопка удалить → modals.open('confirm-delete', {link_id})
 *
 * Props: link_id (передаётся через modals.payload).
 * Lifecycle:
 *   onMounted     → LinksApi.get(id) → fill form
 *   submit        → LinksApi.update(id, {target_url, title, tags, status,
 *                   ttl_expires_at, click_limit}) → modals.emit('edit-link', link)
 *                   → modals.close()
 *
 * Backend (PATCH /api/v1/links/{id}) поддерживает: target_url, title, tags,
 * status, ttl_expires_at, click_limit. Поля без backend'а (slug edit,
 * campaign select, smart-routing, password, internal note) — UI присутствует
 * (1:1 правило), но Submit их не отправляет. Помечены UiDevCard либо
 * комментарием "ожидает backend'а".
 */

import { computed, onMounted, ref } from 'vue';
import { LinksApi, ApiError, type Link } from '@/api/links';
import { useModalStore } from '@/stores/modal';
import UiModal from '@/components/ui/UiModal.vue';
import UiField from '@/components/ui/UiField.vue';
import UiInput from '@/components/ui/UiInput.vue';
import UiButton from '@/components/ui/UiButton.vue';
import UiModalFooter from '@/components/ui/UiModalFooter.vue';
import UiDevCard from '@/components/ui/UiDevCard.vue';

const modals = useModalStore();

// link_id из modals.payload (set'ится при modals.open('edit-link', { link_id }))
const linkId = computed<number | null>(() => {
  const id = modals.payload?.link_id;
  return typeof id === 'number' ? id : null;
});

const loading = ref(true);
const loadError = ref<string | null>(null);
const submitError = ref<string | null>(null);

// Form state
const link = ref<Link | null>(null);
const targetUrl = ref('');
const slug = ref('');
const slugOriginal = ref('');
const title = ref('');
const tags = ref<string[]>([]);
const tagDraft = ref('');
const status = ref<'active' | 'paused'>('active');
const ttlLocal = ref(''); // datetime-local format YYYY-MM-DDTHH:MM
const clickLimit = ref<number | ''>('');
const passwordEnabled = ref(false);
const passwordValue = ref('');
const internalNote = ref('');

// UTM (parsed from current target_url query params; merge обратно при submit)
const utmSource = ref('');
const utmMedium = ref('');
const utmCampaign = ref('');
const utmContent = ref('');
const utmTerm = ref('');

const utmFilledCount = computed(() => {
  return [utmSource, utmMedium, utmCampaign, utmContent, utmTerm]
    .filter((r) => r.value.trim() !== '')
    .length;
});

const linkCode = computed(() => link.value?.slug || '');
const linkCreatedLabel = computed(() => {
  if (!link.value) return '';
  try {
    const d = new Date(link.value.created_at);
    return d.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long' });
  } catch {
    return '';
  }
});

// ─── Lifecycle ─────────────────────────────────────────────────

onMounted(async () => {
  const id = linkId.value;
  if (id === null) {
    loadError.value = 'link_id не передан в payload модалки';
    loading.value = false;
    return;
  }
  try {
    const l = await LinksApi.get(id);
    link.value = l;
    targetUrl.value = stripUtm(l.target_url);
    slug.value = l.slug;
    slugOriginal.value = l.slug;
    title.value = l.title || '';
    tags.value = [...(l.tags || [])];
    status.value = l.status === 'paused' ? 'paused' : 'active';
    ttlLocal.value = isoToLocal(l.ttl_expires_at);
    clickLimit.value = l.click_limit ?? '';
    // Parse UTM params from target_url
    parseUtm(l.target_url);
  } catch (e) {
    loadError.value = e instanceof ApiError
      ? (e.status === 404 ? 'Ссылка не найдена' : e.message)
      : (e as Error).message;
  } finally {
    loading.value = false;
  }
});

// ─── URL / UTM helpers ─────────────────────────────────────────

function stripUtm(url: string): string {
  try {
    const u = new URL(url);
    ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content']
      .forEach((k) => u.searchParams.delete(k));
    return u.toString();
  } catch {
    return url;
  }
}

function parseUtm(url: string): void {
  try {
    const u = new URL(url);
    utmSource.value = u.searchParams.get('utm_source') || '';
    utmMedium.value = u.searchParams.get('utm_medium') || '';
    utmCampaign.value = u.searchParams.get('utm_campaign') || '';
    utmContent.value = u.searchParams.get('utm_content') || '';
    utmTerm.value = u.searchParams.get('utm_term') || '';
  } catch {
    /* fail-quiet — malformed URL */
  }
}

function buildTargetUrl(): string {
  let url = targetUrl.value.trim();
  if (!url) return '';
  if (!/^https?:\/\//i.test(url)) url = 'https://' + url;
  try {
    const u = new URL(url);
    // Сначала очистим существующие utm_* (если они уже были) — потом перепишем.
    ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content']
      .forEach((k) => u.searchParams.delete(k));
    if (utmSource.value.trim()) u.searchParams.set('utm_source', utmSource.value.trim());
    if (utmMedium.value.trim()) u.searchParams.set('utm_medium', utmMedium.value.trim());
    if (utmCampaign.value.trim()) u.searchParams.set('utm_campaign', utmCampaign.value.trim());
    if (utmTerm.value.trim()) u.searchParams.set('utm_term', utmTerm.value.trim());
    if (utmContent.value.trim()) u.searchParams.set('utm_content', utmContent.value.trim());
    return u.toString();
  } catch {
    return url;
  }
}

function isoToLocal(iso: string | null): string {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  } catch {
    return '';
  }
}

function localToIso(local: string): string | null {
  if (!local) return null;
  try {
    return new Date(local).toISOString();
  } catch {
    return null;
  }
}

// ─── Tags multi-chip ───────────────────────────────────────────

function addTag(e: Event) {
  if (e instanceof KeyboardEvent) {
    // Add on Enter or comma
    if (e.key !== 'Enter' && e.key !== ',') return;
    e.preventDefault();
  }
  const v = tagDraft.value.trim().replace(/,$/, '');
  if (!v) return;
  if (!tags.value.includes(v)) tags.value.push(v);
  tagDraft.value = '';
}

function removeTag(t: string) {
  tags.value = tags.value.filter((x) => x !== t);
}

// ─── Submit ────────────────────────────────────────────────────

async function submit() {
  submitError.value = null;
  const id = linkId.value;
  if (id === null) {
    submitError.value = 'link_id отсутствует';
    return;
  }
  const url = buildTargetUrl();
  if (!url) {
    submitError.value = 'Введите целевой URL';
    return;
  }
  modals.setPending(true);
  try {
    const updated = await LinksApi.update(id, {
      target_url: url,
      title: title.value.trim() || null,
      tags: tags.value,
      status: status.value,
      ttl_expires_at: localToIso(ttlLocal.value),
      click_limit: clickLimit.value === '' ? null : Number(clickLimit.value),
    });
    modals.emit('edit-link', updated);
    modals.close();
  } catch (e) {
    submitError.value = (e as Error).message;
  } finally {
    modals.setPending(false);
  }
}

function openDeleteConfirm() {
  if (linkId.value === null) return;
  modals.open('confirm-delete', { link_id: linkId.value, slug: link.value?.slug });
}
</script>

<template>
  <UiModal
    :title="'Редактирование ссылки'"
    size="md"
    :disable-close="modals.pending"
    @close="modals.close"
  >
    <template #header>
      <div class="head-text">
        <h2 id="edit-link-title" class="modal-title">Редактирование ссылки</h2>
        <p class="modal-sub" v-if="link">
          <code class="link-code">{{ linkCode }}</code>
          <span class="modal-sub-sep">·</span>
          <span>создана {{ linkCreatedLabel }}, <strong>{{ link.click_count.toLocaleString('ru-RU') }}</strong> переходов</span>
        </p>
      </div>
    </template>

    <div v-if="loading" class="loading-row">Загружаем…</div>
    <div v-else-if="loadError" class="load-error">
      <strong>Ошибка:</strong> {{ loadError }}
    </div>
    <form v-else id="edit-link-form" class="edit-form" @submit.prevent="submit">
      <!-- 1. Status row -->
      <div class="status-row">
        <div class="status-current">
          <span class="status-dot" :class="status === 'active' ? 'status-dot-active' : 'status-dot-paused'"></span>
          <span class="status-label">{{ status === 'active' ? 'Ссылка активна' : 'Ссылка на паузе' }}</span>
        </div>
        <div class="status-toggle" role="radiogroup" aria-label="Статус">
          <label class="status-opt">
            <input type="radio" name="status" value="active" :checked="status === 'active'" @change="status = 'active'">
            <span>Активна</span>
          </label>
          <label class="status-opt">
            <input type="radio" name="status" value="paused" :checked="status === 'paused'" @change="status = 'paused'">
            <span>На паузе</span>
          </label>
        </div>
      </div>

      <!-- 2. Target URL -->
      <UiField
        label="Целевой URL"
        for="ed-target"
        hint="Изменение целевого URL применяется ко всем будущим переходам, переход в браузере у тех, кто уже открыл — старый URL."
      >
        <UiInput id="ed-target" v-model="targetUrl" type="url" required />
      </UiField>

      <!-- 3. Slug — disabled т.к. backend не поддерживает slug edit + 301-redirect -->
      <UiField label="Короткий код" hint="Изменение кода ожидает backend'а с 301-redirect support.">
        <div class="slug-row">
          <span class="slug-prefix">link.helmix.app/</span>
          <input id="ed-slug" v-model="slug" class="input" type="text" maxlength="32" disabled />
        </div>
      </UiField>

      <!-- 4. Campaign + Tags row -->
      <div class="field-row">
        <!-- Campaign select — UI на 1:1, backend campaign_id PATCH не поддерживает в LinkUpdate -->
        <UiField label="Кампания" hint="Привязка кампании ожидает backend'а.">
          <select id="ed-campaign" class="input" disabled>
            <option value="">— без кампании —</option>
          </select>
        </UiField>
        <UiField label="Теги">
          <div class="tag-input">
            <span v-for="t in tags" :key="t" class="tag-chip">
              {{ t }}
              <button type="button" class="tag-remove" :aria-label="`Удалить ${t}`" @click="removeTag(t)">×</button>
            </span>
            <input
              class="tag-add-input"
              v-model="tagDraft"
              type="text"
              placeholder="+ тег"
              @keydown="addTag"
              @blur="addTag"
            />
          </div>
        </UiField>
      </div>

      <!-- 5. UTM section -->
      <details class="section" open>
        <summary class="section-summary">
          <svg class="caret" viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2">
            <polyline points="9 18 15 12 9 6"/>
          </svg>
          <span class="section-title">UTM-метки</span>
          <span class="section-badge">{{ utmFilledCount }} заполнено</span>
        </summary>
        <div class="section-body">
          <div class="field-grid">
            <UiField label="utm_source" for="utm-src">
              <UiInput id="utm-src" v-model="utmSource" />
            </UiField>
            <UiField label="utm_medium" for="utm-med">
              <UiInput id="utm-med" v-model="utmMedium" />
            </UiField>
            <UiField label="utm_campaign" for="utm-cam">
              <UiInput id="utm-cam" v-model="utmCampaign" />
            </UiField>
            <UiField label="utm_content" for="utm-cont">
              <UiInput id="utm-cont" v-model="utmContent" />
            </UiField>
            <UiField label="utm_term — опционально" for="utm-term" class="field-full">
              <UiInput id="utm-term" v-model="utmTerm" />
            </UiField>
          </div>
        </div>
      </details>

      <!-- 6. Smart routing (read-only, backend pending) -->
      <details class="section">
        <summary class="section-summary">
          <svg class="caret" viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2">
            <polyline points="9 18 15 12 9 6"/>
          </svg>
          <span class="section-title">Умная маршрутизация</span>
          <span class="section-badge">скоро</span>
        </summary>
        <div class="section-body">
          <UiDevCard
            feature="Умная маршрутизация"
            message="Условные правила (страна / устройство / время) — ожидают backend'а (routing_rules table)."
          />
        </div>
      </details>

      <!-- 7. Advanced — TTL / click_limit (real backend) + password / note (DevCard) -->
      <details class="section">
        <summary class="section-summary">
          <svg class="caret" viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2">
            <polyline points="9 18 15 12 9 6"/>
          </svg>
          <span class="section-title">Дополнительно</span>
          <span class="section-badge">TTL · пароль</span>
        </summary>
        <div class="section-body">
          <div class="field-row">
            <UiField label="Действует до (TTL)" for="ed-ttl" hint="После даты ссылка отдаст 410 Gone.">
              <input id="ed-ttl" v-model="ttlLocal" class="input" type="datetime-local" />
            </UiField>
            <UiField label="Лимит переходов" for="ed-max" hint="Опционально — закрыть ссылку после N кликов.">
              <input id="ed-max" v-model="clickLimit" class="input" type="number" min="1" placeholder="без лимита" />
            </UiField>
          </div>

          <div class="toggle-row">
            <label class="toggle-wrap">
              <input type="checkbox" v-model="passwordEnabled" disabled />
              <span class="toggle-text">
                <span class="toggle-title">Защита паролем</span>
                <span class="toggle-desc">Пользователь введёт пароль перед редиректом. Ожидает backend'а.</span>
              </span>
            </label>
          </div>
          <UiField v-if="passwordEnabled" label="Пароль" for="ed-password">
            <UiInput id="ed-password" v-model="passwordValue" type="password" disabled />
          </UiField>

          <UiField
            label="Внутренняя заметка — только для команды"
            for="ed-note"
            hint="Поле ожидает backend'а (Link.internal_note column)."
          >
            <textarea id="ed-note" v-model="internalNote" class="input textarea" rows="2" disabled />
          </UiField>
        </div>
      </details>

      <!-- 8. Danger zone -->
      <details class="section section-danger">
        <summary class="section-summary">
          <svg class="caret" viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2">
            <polyline points="9 18 15 12 9 6"/>
          </svg>
          <span class="section-title">Опасная зона</span>
        </summary>
        <div class="section-body">
          <div class="danger-row">
            <div class="danger-text">
              <div class="danger-title">Удалить ссылку</div>
              <div class="danger-sub">
                Удаление навсегда, отменить нельзя. Накопленная статистика
                сохранится в архиве кампании.
              </div>
            </div>
            <UiButton variant="danger" type="button" @click="openDeleteConfirm">Удалить</UiButton>
          </div>
        </div>
      </details>

      <p v-if="submitError" class="form-error">{{ submitError }}</p>
    </form>

    <template #footer>
      <UiModalFooter
        save-type="submit"
        save-form="edit-link-form"
        :save-loading="modals.pending"
        :save-disabled="loading || !!loadError"
        :cancel-disabled="modals.pending"
        @cancel="modals.close"
      />
    </template>
  </UiModal>
</template>

<style scoped>
.head-text { display: flex; flex-direction: column; gap: 4px; }
.modal-title { margin: 0; font-size: 18px; letter-spacing: -0.01em; }
.modal-sub {
  display: flex; flex-wrap: wrap; align-items: center; gap: 6px;
  font-size: 13px; line-height: 1.45; margin: 0;
  color: var(--fg-muted, #9aa5b1);
}
.modal-sub strong { color: var(--fg, #e6eaf0); font-weight: 600; }
.modal-sub-sep { color: var(--fg-dim, #6b7480); }
.link-code {
  background: var(--bg, #0f1620);
  padding: 2px 8px;
  border-radius: 4px;
  font-family: var(--font-mono, ui-monospace, monospace);
  font-size: 12px;
  color: var(--accent-hi, #6f9eff);
}

.loading-row, .load-error {
  padding: 24px 0; text-align: center; color: var(--fg-muted, #9aa5b1);
}
.load-error strong { color: #ff5d8f; }

.edit-form { display: flex; flex-direction: column; gap: 16px; }

/* Status row */
.status-row {
  display: flex; align-items: center; justify-content: space-between;
  gap: 12px; padding: 10px 12px;
  background: var(--bg-card, #1a1f29);
  border: 1px solid var(--border, #2a3340);
  border-radius: 8px;
}
.status-current { display: flex; align-items: center; gap: 8px; font-size: 13px; }
.status-dot {
  width: 8px; height: 8px; border-radius: 50%;
  box-shadow: 0 0 8px currentColor;
}
.status-dot-active { background: #3dd87f; color: #3dd87f; }
.status-dot-paused { background: #f0b94e; color: #f0b94e; }
.status-label { color: var(--fg, #e6eaf0); font-weight: 500; }
.status-toggle {
  display: flex; padding: 2px;
  background: var(--bg, #0f1620);
  border: 1px solid var(--border, #2a3340);
  border-radius: 6px;
}
.status-opt { position: relative; }
.status-opt input { position: absolute; opacity: 0; pointer-events: none; }
.status-opt span {
  display: block; padding: 4px 12px; font-size: 12.5px;
  color: var(--fg-muted, #9aa5b1); border-radius: 4px;
  cursor: pointer; transition: 0.1s;
}
.status-opt input:checked + span {
  background: #4f8cff; color: #fff;
}

/* Field rows */
.field-row { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; }

/* Slug input */
.slug-row {
  display: flex; align-items: stretch;
  border: 1px solid var(--border, #2a3340);
  border-radius: 7px;
  background: var(--bg-card, #1a1f29);
  overflow: hidden;
}
.slug-prefix {
  padding: 8px 12px; background: var(--bg, #0f1620);
  color: var(--fg-muted, #9aa5b1); font-size: 13px;
  font-family: var(--font-mono, ui-monospace, monospace);
  display: flex; align-items: center;
  border-right: 1px solid var(--border, #2a3340);
}
.slug-row .input {
  flex: 1; border: 0; background: transparent; border-radius: 0;
  font-family: var(--font-mono, ui-monospace, monospace);
  padding: 8px 12px; color: var(--fg, #e6eaf0); font-size: 13px;
}
.slug-row .input:disabled { opacity: 0.6; cursor: not-allowed; }

/* Tags multi-chip */
.tag-input {
  display: flex; flex-wrap: wrap; gap: 6px; padding: 8px;
  border: 1px solid var(--border, #2a3340); border-radius: 7px;
  background: var(--bg-card, #1a1f29);
}
.tag-chip {
  display: inline-flex; align-items: center; gap: 4px;
  padding: 3px 8px; background: rgba(79,140,255,0.12);
  color: var(--accent-hi, #6f9eff);
  border-radius: 12px; font-size: 12px;
}
.tag-remove {
  background: transparent; border: 0; color: inherit;
  font-size: 14px; line-height: 1; cursor: pointer; padding: 0;
}
.tag-add-input {
  flex: 1; min-width: 80px; background: transparent; border: 0;
  color: var(--fg, #e6eaf0); font-size: 13px; outline: none;
}

/* Sections (details/summary collapsible) */
.section {
  border: 1px solid var(--border, #2a3340);
  border-radius: 8px;
  background: var(--bg-card, #1a1f29);
}
.section[open] > .section-summary .caret { transform: rotate(90deg); }
.section-summary {
  display: flex; align-items: center; gap: 8px;
  padding: 10px 14px; cursor: pointer; list-style: none;
  user-select: none;
}
.section-summary::-webkit-details-marker { display: none; }
.caret { color: var(--fg-muted, #9aa5b1); transition: transform 0.15s; }
.section-title { flex: 1; font-size: 13px; font-weight: 600; color: var(--fg, #e6eaf0); }
.section-badge {
  font-size: 11.5px; color: var(--fg-muted, #9aa5b1);
  padding: 2px 8px; border-radius: 10px;
  background: var(--bg, #0f1620);
}
.section-body {
  padding: 0 14px 14px;
  display: flex; flex-direction: column; gap: 12px;
}

.field-grid {
  display: grid; grid-template-columns: 1fr 1fr; gap: 12px;
}
.field-full { grid-column: 1 / -1; }

/* Inputs reuse global .input style — fallback */
.input {
  padding: 9px 12px; background: var(--bg, #0f1620);
  border: 1px solid var(--border, #2a3340); border-radius: 7px;
  color: var(--fg, #e6eaf0); font-size: 14px; font-family: inherit;
}
.input:disabled { opacity: 0.55; cursor: not-allowed; }
.textarea { resize: vertical; min-height: 56px; }

/* Toggle */
.toggle-row { padding: 4px 0; }
.toggle-wrap { display: flex; gap: 10px; align-items: flex-start; cursor: pointer; }
.toggle-wrap input { margin-top: 3px; }
.toggle-text { display: flex; flex-direction: column; gap: 2px; }
.toggle-title { font-size: 13px; font-weight: 600; color: var(--fg, #e6eaf0); }
.toggle-desc { font-size: 12px; color: var(--fg-muted, #9aa5b1); }

/* Danger zone */
.section-danger { border-color: rgba(255,93,143,0.3); }
.danger-row { display: flex; gap: 14px; align-items: center; justify-content: space-between; }
.danger-text { flex: 1; }
.danger-title { font-size: 13px; font-weight: 600; color: var(--fg, #e6eaf0); margin-bottom: 4px; }
.danger-sub { font-size: 12px; color: var(--fg-muted, #9aa5b1); line-height: 1.4; }

.form-error {
  padding: 8px 12px; font-size: 12.5px;
  color: #ff5d8f; background: rgba(255,93,143,0.08);
  border-radius: 6px;
}

@media (max-width: 520px) {
  .field-row, .field-grid { grid-template-columns: 1fr; }
}
</style>
